/**
 * GL engine and scene (03 §4, 09 §6): one WebGL2 `Engine` + one `Scene`, the device-px backbuffer
 * at render scale r, and the `UniformHub` that pushes shared uniforms (`uPxScale = k / r`, `uTime`,
 * backbuffer and per-camera viewport sizes) into core **and style-owned** `ShaderMaterial`s.
 */
import { Engine } from '@babylonjs/core/Engines/engine';
import '@babylonjs/core/Engines/Extensions/engine.dynamicTexture';
import { Tools } from '@babylonjs/core/Misc/tools';
import { Scene } from '@babylonjs/core/scene';
import { Color4 } from '@babylonjs/core/Maths/math.color';
import { Vector2 } from '@babylonjs/core/Maths/math.vector';
import type { Camera } from '@babylonjs/core/Cameras/camera';
import type { ShaderMaterial } from '@babylonjs/core/Materials/shaderMaterial';
import type { Observer } from '@babylonjs/core/Misc/observable';
import type { RGBA, ScaleState } from '../api.ts';

/** Shared uniforms every hub-registered material receives (declare them in the material options). */
export const HUB_UNIFORMS: readonly string[] = ['uPxScale', 'uTime', 'uBackbufferPx', 'uViewportPx', 'uViewportOrigin'];

/** Layer masks (03 §10): every core mesh is MAIN-only unless asked otherwise. */
export const LAYER = { MAIN: 0x1, INSET: 0x2 } as const;

/**
 * Cross-origin textures (plan 10, review R-finding): bundle data may be served from another origin
 * (the R2 data host), so every Babylon image load must be CORS-anonymous or the WebGL upload
 * taints/fails. Babylon's `LoadImage` applies `Tools.CorsBehavior` to each `<img>` it creates; the
 * two `<img>`-based texture loads in the codebase (undercut `gl/cars.ts` `makeTexture` and
 * `gl/inset.ts` `obtainHiresTexture`, both `new Texture(url, …)`) rely on exactly this. Babylon 9
 * already defaults `FileToolsOptions.CorsBehavior` to 'anonymous' — pin it once at engine creation
 * so a Babylon upgrade can never silently drop it (unit-tested in gl-cors.test.ts). Fetch-based
 * loads (tiles, chunks, fonts) are unaffected: fetch → ImageBitmap/blob never taints.
 */
export function pinCorsBehavior(): void {
  Tools.CorsBehavior = 'anonymous';
}

/** Rendering groups used by the core layers (depth is cleared between groups). */
export const GROUP = { TILES: 0, VECTORS: 1, LINES: 2, CARS: 3 } as const;

/** Per-camera viewport info the hub publishes before that camera renders. */
export interface CameraViewport {
  /** backbuffer px, origin bottom-left (GL convention) */
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Shared-uniform registry (09 §12). Vector uniforms are shared `Vector2` objects, so one mutation
 * updates every material; floats are pushed to each registered material on change.
 */
export class UniformHub {
  pxScale = 1;
  time = 0;
  readonly backbufferPx = new Vector2(1, 1);
  /** viewport of the camera currently rendering (backbuffer px) */
  readonly viewportPx = new Vector2(1, 1);
  readonly viewportOrigin = new Vector2(0, 0);
  private readonly mats: ShaderMaterial[] = [];
  private readonly cams = new Map<Camera, CameraViewport>();
  private camObs: Observer<Camera> | null = null;

  /** Register a material: it gets the HUB_UNIFORMS now and on every change. Returns an unregister fn. */
  register(mat: ShaderMaterial): () => void {
    if (!this.mats.includes(mat)) this.mats.push(mat);
    mat.setFloat('uPxScale', this.pxScale);
    mat.setFloat('uTime', this.time);
    mat.setVector2('uBackbufferPx', this.backbufferPx);
    mat.setVector2('uViewportPx', this.viewportPx);
    mat.setVector2('uViewportOrigin', this.viewportOrigin);
    const off = (): void => {
      const i = this.mats.indexOf(mat);
      if (i >= 0) this.mats.splice(i, 1);
    };
    mat.onDisposeObservable.addOnce(off);
    return off;
  }

  setPxScale(v: number): void {
    if (v === this.pxScale) return;
    this.pxScale = v;
    for (let i = 0; i < this.mats.length; i++) this.mats[i]?.setFloat('uPxScale', v);
  }

  setTime(t: number): void {
    this.time = t;
    for (let i = 0; i < this.mats.length; i++) this.mats[i]?.setFloat('uTime', t);
  }

  setBackbuffer(w: number, h: number): void {
    this.backbufferPx.set(w, h);
  }

  private entry(cam: Camera): CameraViewport {
    let e = this.cams.get(cam);
    if (!e) {
      e = { x: 0, y: 0, w: 0, h: 0 };
      this.cams.set(cam, e);
    }
    return e;
  }

  /** Cameras publish their viewport here (backbuffer px, bottom-left origin). Allocation-free. */
  setCameraViewport(cam: Camera, x: number, y: number, w: number, h: number): void {
    const e = this.entry(cam);
    if (e.x === x && e.y === y && e.w === w && e.h === h) return;
    e.x = x;
    e.y = y;
    e.w = w;
    e.h = h;
  }
  getCameraViewport(cam: Camera): Readonly<CameraViewport> | undefined {
    return this.cams.get(cam);
  }
  removeCamera(cam: Camera): void {
    this.cams.delete(cam);
  }

  /** Hook the scene so the per-camera viewport uniforms are current while each camera renders. */
  attach(scene: Scene): void {
    if (this.camObs) return;
    this.camObs = scene.onBeforeCameraRenderObservable.add((cam) => {
      const vp = this.cams.get(cam);
      if (vp) {
        this.viewportPx.set(vp.w, vp.h);
        this.viewportOrigin.set(vp.x, vp.y);
      } else {
        this.viewportPx.copyFrom(this.backbufferPx);
        this.viewportOrigin.set(0, 0);
      }
    });
  }
  detach(scene: Scene): void {
    if (this.camObs) scene.onBeforeCameraRenderObservable.remove(this.camObs);
    this.camObs = null;
  }
}

/** The event-target subset the H6 context-loss listeners attach to (the GL canvas; a fake in tests). */
export type ContextEventTarget = Pick<EventTarget, 'addEventListener' | 'removeEventListener'>;

/**
 * H6: `webglcontextlost`/`webglcontextrestored` on the GL canvas. Babylon's own listeners
 * preventDefault and re-derive its state on restore (effects, geometries, engine-owned
 * textures); this keeps the preventDefault explicit (without it the context stays dead) and
 * calls `onRestored` after the restore. The one thing Babylon cannot restore is the tile caches'
 * raw `TEXTURE_2D_ARRAY` pages (created with raw GL calls; Babylon would rebuild them EMPTY):
 * each TileCache drops and re-fetches its tiles from its own `onContextRestoredObservable`
 * observer (`attachContextEvents`, core/gl/tiles.ts).
 *
 * Returns the detach closure so `GlEngine.dispose()` can drop the listeners (HPF3): the canvas
 * can outlive the engine, and a restore after dispose would drive the disposed engine.
 */
export function attachContextEvents(target: ContextEventTarget, onRestored: () => void): () => void {
  const lost = (e: Event): void => {
    e.preventDefault(); // opt in to webglcontextrestored
  };
  const restored = (): void => {
    onRestored();
  };
  target.addEventListener('webglcontextlost', lost, false);
  target.addEventListener('webglcontextrestored', restored, false);
  return () => {
    target.removeEventListener('webglcontextlost', lost, false);
    target.removeEventListener('webglcontextrestored', restored, false);
  };
}

export interface GlEngineOptions {
  /** scene clear colour (0..255 RGB, alpha 0..1); default black */
  clear?: RGBA;
}

/** Engine + scene + hub, with the scale state applied. */
export class GlEngine {
  readonly engine: Engine;
  readonly scene: Scene;
  readonly hub = new UniformHub();
  scale: ScaleState;
  private canvasDevice: { w: number; h: number } | undefined;
  private readonly listeners: ((s: ScaleState) => void)[] = [];
  /** H6 listener detach (HPF3: dispose drops the pair; see attachContextEvents above) */
  private detachCtxEvents: (() => void) | null = null;

  constructor(canvas: HTMLCanvasElement, scale: ScaleState, opts: GlEngineOptions = {}) {
    pinCorsBehavior(); // before any texture can load: see the doc block above
    // WebGL2 Engine (D7): antialias off, stencil on, no preserved drawing buffer, no automatic DPR.
    this.engine = new Engine(
      canvas,
      false,
      { preserveDrawingBuffer: false, stencil: true, antialias: false, disableWebGL2Support: false, premultipliedAlpha: false },
      false,
    );
    if (this.engine.webGLVersion < 2) {
      this.engine.dispose();
      throw new Error('Undercut needs WebGL2');
    }
    // H6: loss/restored on the GL canvas; the restore re-pins our backbuffer and hub uniforms
    this.detachCtxEvents = attachContextEvents(canvas, () => {
      this.applyScale(this.scale, this.canvasDevice);
    });
    this.scene = new Scene(this.engine);
    this.scene.skipPointerMovePicking = true; // picking is analytic (03 §4)
    this.scene.autoClear = true;
    this.setClear(opts.clear ?? [0, 0, 0, 1]);
    this.hub.attach(this.scene);
    this.scale = scale;
    this.applyScale(scale);
  }

  setClear(c: RGBA): void {
    this.scene.clearColor = new Color4(c[0] / 255, c[1] / 255, c[2] / 255, c[3]);
  }

  /**
   * Apply a scale state (09 §6): backbuffer = device px / r (`setHardwareScalingLevel(r)` plus an
   * explicit size, never derived from CSS), `uPxScale = k / r`. `canvasDevice` is the GL canvas
   * size in device px when it differs from `scale.device`; the canvas top-left must be the stage
   * top-left.
   */
  applyScale(scale: ScaleState, canvasDevice?: { w: number; h: number }): void {
    this.scale = scale;
    if (canvasDevice) this.canvasDevice = canvasDevice; // remembered: a context restore re-pins it (H6)
    const dev = canvasDevice ?? this.canvasDevice ?? scale.device;
    const r = scale.r > 0 ? scale.r : 1;
    const w = Math.max(1, Math.round(dev.w / r));
    const h = Math.max(1, Math.round(dev.h / r));
    // setHardwareScalingLevel calls resize() (CSS-derived); setSize then pins the exact size.
    if (this.engine.getHardwareScalingLevel() !== r) this.engine.setHardwareScalingLevel(r);
    this.engine.setSize(w, h, true);
    this.hub.setBackbuffer(this.engine.getRenderWidth(), this.engine.getRenderHeight());
    this.hub.setPxScale(scale.k / r);
    for (let i = 0; i < this.listeners.length; i++) this.listeners[i]?.(scale);
  }

  /** backbuffer px per stage px (= uPxScale) */
  get pxScale(): number {
    return this.hub.pxScale;
  }

  /** Notified after every applyScale (cameras re-derive their viewports). */
  onScale(fn: (s: ScaleState) => void): () => void {
    this.listeners.push(fn);
    return () => {
      const i = this.listeners.indexOf(fn);
      if (i >= 0) this.listeners.splice(i, 1);
    };
  }

  dispose(): void {
    // HPF3: drop the H6 canvas listeners before the engine goes: the canvas outlives the engine,
    // and a restore after dispose would applyScale on the disposed engine
    this.detachCtxEvents?.();
    this.detachCtxEvents = null;
    this.hub.detach(this.scene);
    this.scene.dispose();
    this.engine.dispose();
  }
}

export function createEngine(canvas: HTMLCanvasElement, scale: ScaleState, opts?: GlEngineOptions): GlEngine {
  return new GlEngine(canvas, scale, opts);
}
